"use client"

import Link from "next/link"
import Image from "next/image"
import { getAuthor } from "@/lib/authors"
import { PUBLIC_SOUND_COUNT_LABEL } from "@/lib/public-sound-count"

interface AuthorCardProps {
  /** Byline string from blog-data.ts `author` (resolved via getAuthor). */
  name: string
  className?: string
}

/**
 * Author byline card shown at the foot of blog posts and in the sidebar.
 *
 * Identity is resolved from the canonical registry in lib/authors.ts — never
 * synthesized here. A real person (isPerson) gets their avatar + verified
 * profile links; the house editorial byline (org) gets the brand tile and no
 * person-shaped "follow me" affordance. Only links actually present on the
 * author record are rendered, each pointing at the author's real profile.
 */
export function AuthorCard({ name, className }: AuthorCardProps) {
  const author = getAuthor(name)

  return (
    <div className={className}>
      <p className="tls-eyebrow mb-3">
        Written by
      </p>
      <div className="flex items-center gap-3">
        {author.isPerson && author.avatarUrl ? (
          <Image
            src={author.avatarUrl}
            alt={author.name}
            width={40}
            height={40}
            className="size-10 rounded-[var(--radius-pill)] object-cover"
            unoptimized
          />
        ) : (
          <div className="size-10 bg-[var(--tls-surface-elevated)] border border-[var(--tls-border-subtle)] rounded-[var(--radius-pill)] flex items-center justify-center text-[var(--tls-brand)] font-bold text-sm">
            {author.isPerson ? author.name.charAt(0) : "T"}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-[var(--tls-fg-strong)] font-medium text-sm truncate">{author.name}</p>
          <p className="text-[var(--tls-fg-subtle)] text-xs truncate">{author.role}</p>
        </div>
        {author.isPerson && author.twitterUrl && (
          <a
            href={author.twitterUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--tls-fg-subtle)] hover:text-[var(--tls-brand)] transition-colors"
            aria-label={`${author.name} on X`}
          >
            {/* X (Twitter) logo — inline SVG since lucide-react 1.x removed brand icons */}
            <svg className="size-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </a>
        )}
      </div>
    </div>
  )
}

// Promotional card for the sidebar
export function PromoCard({
  className,
  soundCountLabel = PUBLIC_SOUND_COUNT_LABEL,
}: {
  className?: string
  soundCountLabel?: string
}) {
  return (
    <div className={className}>
      {/* A sidebar callout is a hairline-separated block: one rule above,
          no fill, no four-sided container. The previous --tls-surface fill
          made it a filled card by the structural de-card predicate (owner
          ruling 2026-08-15 retired ink-rule callouts; hairlines stay). */}
      <div className="border-t border-[color:var(--tls-border-subtle)] pt-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[var(--tls-brand)] text-xs font-bold">T</span>
          <span className="text-sm font-semibold text-[var(--tls-fg-strong)]">Tesla Lock Sound</span>
        </div>
        <p className="text-xs text-[var(--tls-fg-muted)] mb-3">
          Browse {soundCountLabel} Tesla lock sounds: free WAV downloads, instant preview.
        </p>
        <Link
          href="/sounds"
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-[var(--tls-brand)] text-[var(--tls-brand-fg)] text-xs font-medium rounded-[var(--radius-pill)] hover:bg-[var(--tls-brand-hover)] transition-colors"
        >
          Browse Sounds
          <span className="text-[0.625rem]">→</span>
        </Link>
      </div>
    </div>
  )
}
